import { io, type Socket } from 'socket.io-client';
import { getNewApiBaseUrl } from '@/lib/api-common/config';

let socket: Socket | null = null;
let pingInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Socket.IO 클라이언트 초기화.
 * - 로컬(DEV): 동일 origin → Vite '/api-v2/socket.io' 프록시가 v2 BE로 전달
 * - 운영(BUILD): VITE_NEW_API_BASE_URL(v2 오리진)로 직접 연결
 * 중복 호출 시 기존 연결을 재사용한다.
 */
/**
 * Vite proxy path for the v2 socket in local dev — shares the /api-v2 namespace
 * with the v2 REST routes (the proxy strips /api-v2 → BE '/socket.io').
 */
const V2_SOCKET_PROXY_PATH = '/api-v2/socket.io';

/**
 * Resolve the Socket.IO connection target — v2 only (legacy escape hatch removed
 * in the v2-only migration).
 * - ABSOLUTE base (deployed, VITE_NEW_API_BASE_URL=https://…): connect directly to
 *   the v2 BE origin with credentials so `adminconnect.sid` rides the cross-site
 *   handshake (BE sets SameSite=None;Secure + CORS credentials). Default
 *   '/socket.io' path — no /api-v2 prefix on the BE origin.
 * - relative base (local dev, '/api-v2'): connect same-origin (url:undefined) with
 *   path '/api-v2/socket.io' so the Vite proxy forwards to the v2 BE in the same
 *   namespace as the REST calls; the session cookie rides the same-origin request.
 */
function resolveSocketTarget(): {
  url: string | undefined;
  withCredentials: boolean;
  path: string | undefined;
} {
  const base = getNewApiBaseUrl();
  const isAbsolute = /^https?:\/\//.test(base);
  return {
    url: isAbsolute ? base : undefined,
    withCredentials: true,
    path: isAbsolute ? undefined : V2_SOCKET_PROXY_PATH,
  };
}

/**
 * Create the Socket.IO instance WITHOUT opening a connection (autoConnect:false).
 * The handshake is opened only after authentication via connectSocket(), so an
 * unauthenticated app never opens or polls the socket. Idempotent — reuses the
 * existing instance so subscribers (e.g. Header's NEW_NOTIFICATION) attach to a
 * stable object regardless of connection state.
 */
export function initSocket(): Socket {
  if (socket) return socket;

  const { url, withCredentials, path } = resolveSocketTarget();
  socket = io(url, {
    withCredentials,
    // Local-dev v2 routes through the Vite '/api-v2/socket.io' proxy; deployed +
    // legacy use socket.io's default '/socket.io' path.
    ...(path ? { path } : {}),
    // Do not connect until authenticated (connectSocket()); prevents an
    // unauthenticated app from endlessly polling the socket transport.
    autoConnect: false,
    // 재연결 전략: 최대 5회, 1~5초 사이 지수 백오프 (실패하면 더 이상 재시도 안 함)
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  socket.on('connect', () => {
    if (import.meta.env.DEV) {
      console.log('[socket] connected:', socket?.id);
    }

    // keepalive ping (원본 호환: 10초 간격)
    if (pingInterval) clearInterval(pingInterval);
    pingInterval = setInterval(() => {
      socket?.emit('ping');
    }, 10_000);
  });

  socket.on('disconnect', (reason) => {
    if (import.meta.env.DEV) {
      console.log('[socket] disconnected:', reason);
    }
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
  });

  return socket;
}

/**
 * Open the socket connection — call ONLY after authentication. The (cross-site)
 * session cookie rides the handshake. No-op if already connected. Reuses the
 * instance created by initSocket() so existing subscriptions stay attached.
 */
export function connectSocket(): Socket {
  const s = initSocket();
  if (!s.connected) s.connect();
  return s;
}

export function getSocket(): Socket | null {
  return socket;
}

/**
 * Close the connection (e.g. on logout / when unauthenticated). Keeps the
 * instance so subscribers stay attached and a later connectSocket() reopens it.
 */
export function disconnectSocket(): void {
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = null;
  }
  socket?.disconnect();
}
